import { useTheme } from '@mui/material/styles';

import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import {Box, Button, Card, CardContent, Divider, Link, List, ListItem, ListItemText, Stack, useMediaQuery} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import YouTubeIcon from '@mui/icons-material/YouTube';

import PageBreadcrumbs from '../../../components/PageBreadcrumbs';
import BankList from '../../Filers/components/GettingStarted/components/BankList';

const candidateOffices = [
  {
    type: 'Statewide Offices',
    description: 'The Constitutional Offices: Governor, Lieutenant Governor, Attorney General, State Secretary, State Treasurer or State Auditor.'
  },
  {
    type: 'House & Senate',
    description: 'A candidate for the General Court is seeking a seat in the State House or State Senate.'
  },
  {
    type: "The Governor's Council",
    description: 'One of the eight district seats of the Executive Council.'
  },
  {
    type: 'County and District Offices',
    description: 'County Clerks of Court, Commissioners, and Treasurers; Registers of Deeds and Probate; District Attorneys; Sheriffs and Suffolk County Clerks of the Superior and Supreme Judicial Courts'
  },
  {
    type: 'City Council Seats in Cities with Populations of 65,000 or More',
    description: 'This includes the communities of Boston, Brockton, Cambridge, Fall River, Framingham, Haverhill, Lawrence, Lowell, Lynn, Malden, New Bedford, Newton, Quincy, Somerville, Springfield, Waltham and Worcester.'
  },
  {
    type: 'Mayor',
    description: 'All candidates for mayor file campaign finance reports with OCPF.'
  },
  {
    type: 'State Political Parties',
    description: 'The state committees of political parties organized under M.G.L. chapter 52, section 1.'
  },
  {
    type: 'Political Action Committees',
    description: 'Federal or out-of-state PACs that wish to support or oppose Massachusetts candidates must organize a separate Massachusetts PAC with OCPF. Once organized, the Massachusetts PAC must ensure that all of its fundraising activities comply with the Massachusetts campaign finance law. Funds raised must be kept in a separate bank account established for the Massachusetts PAC. No transfers may be made from the Federal PAC or out-of-state PAC to the Massachusetts PAC.'
  }
];


const BankSupport = (): JSX.Element => {

    const theme = useTheme();
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Main>
      <Container>
        <PageBreadcrumbs></PageBreadcrumbs>
        <Typography
            variant="h5"
            align={'left'}
            data-aos={'fade-up'}
            sx={{
                fontWeight: 700,
            }}
            paddingTop={3}
            gutterBottom
            marginBottom={2}
            >
            Bank Support <Typography component="span" variant="caption" color="text.secondary">The information page for financial institutions serving as depositories of campaign accounts</Typography>
        </Typography>
        <Divider/>
        <Grid container spacing={2}>
          <Grid size={{xs:12, md:9}}>
            <Box marginTop={3} marginBottom={4}>
              {/*Qualifying Financial Institutions*/}
               <Box marginBottom={4}>
                  <Typography
                  variant="subtitle1"
                  sx={{
                    textTransform: 'uppercase',
                  }}
                  fontWeight={700}
                  gutterBottom
                  color={'secondary'}
                  align={'center'}
                >
                  Qualifying Financial Institutions
                </Typography>
                <Box  
                  sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    padding: 2,
                    borderRadius: 2,
                    boxShadow: 2,
                  }}>
                <Typography component={'div'} variant='subtitle1'>
                  <span>
                    Section 19 of Chapter 55, the Massachusetts Campaign Finance Law, requires certain candidates to designate a financial institution as a depository for their campaign funds. In order to accept this designation, the financial institution must be:
                  </span>
                  <ul>
                    <li>a national bank, federal savings bank, federal savings and loan association or federal credit union; provided, however, that the bank, association or credit union may transact business and has its main office or a branch office in the commonwealth; or</li>
                    <li>a trust company, credit union, co-operative bank or savings bank; provided, however, that the company, credit union or bank is organized and exists under the laws of the commonwealth or any other state or otherwise may transact business in the commonwealth and has its main office or a branch office in the commonwealth.</li>
                  </ul>
                </Typography>
                </Box>
              </Box>
              {/*Bank Reporter*/}
              <Box marginBottom={4}>
                <Typography
                    variant="subtitle1"
                    sx={{
                      textTransform: 'uppercase',
                    }}
                    fontWeight={700}
                    gutterBottom
                    color={'secondary'}
                    align={'center'}
                  >
                  OCPF Bank Reporter
                </Typography>
                <Box>
                  <Card sx={{ boxShadow: 2}}>
                  <CardContent sx={{ padding: { sm: 4 } }}>
                    <Grid container spacing={4}>
                      <Grid size={{xs:12, md:6}}>
                        <Box marginBottom={4}>
                          <Typography fontWeight={550} variant={'subtitle1'} gutterBottom>
                            E-File Bank Reports
                          </Typography>
                          <Typography variant='subtitle1'>Banks are required to use OCPF Bank Reporter to e-file candidates' bank reports.</Typography>
                          <Typography marginTop={2} variant='subtitle1'>First-time users will need to set up the user ID / password to 
                            use Bank Reporter. If you do not receive your login information yet, 
                            please contact OCPF Bank Liaison (Sarabeth McClain at ocpf@mass.gov).
                          </Typography>
                        </Box>
                        <Button component={Link} href={'https://www.ocpfreporter.us/Account/LogOn'} target="_blank" size={'large'} variant={'contained'}>
                            Log on to Bank Reporter
                          </Button>
                      </Grid>
                      <Grid size={{xs:12, md:6}}>
                        <Box
                          width={1}
                          height={1}
                          borderLeft={{ xs: 0, sm: `1px solid ${theme.palette.divider}` }}
                          paddingLeft={{ xs: 0, sm: 4 }}
                          paddingTop={{ xs: 4, sm: 0 }}
                          borderTop={{ xs: `1px solid ${theme.palette.divider}`, sm: 0 }}
                        >
                          <Box marginBottom={4}>
                            <Typography fontWeight={550} variant={'subtitle1'} gutterBottom>
                              Bank Reporter Resources
                            </Typography>
                            <Stack direction='row' spacing={2} marginTop={2}>
                              <PictureAsPdfIcon fontSize='small' />
                              <Typography variant='subtitle2' underline='none' component={Link} href={'https://ocpf2.blob.core.windows.net/pdf/forms/cpf_d103.pdf'} target="_blank">OCPF Form: Appointment of Depository CPF D103</Typography>
                            </Stack>
                            <Stack direction='row' spacing={2} marginTop={2}>
                              <PictureAsPdfIcon fontSize='small' />
                              <Typography variant='subtitle2' underline='none' component={Link} href={'https://ocpf2.blob.core.windows.net/pdf/guides/bank_reporter_manual.pdf'} target="_blank">Bank Reporter User Manual</Typography>
                            </Stack>
                            <Stack direction='row' spacing={2} marginTop={2}>
                              <YouTubeIcon fontSize='small' />
                              <Typography variant='subtitle2' underline='none' component={Link} href={'https://www.youtube.com/watch?v=TcLnbPoNBmo'} target="_blank">Training Video: How to Use Bank Reporter</Typography>
                            </Stack>
                          </Box>
                        </Box>
                      </Grid>
                  </Grid>
                </CardContent>
              </Card>
                </Box>
              </Box>
              {/*Depository Filers*/}
              <Box marginBottom={4}>
                <Typography
                    variant="subtitle1"
                    sx={{
                      textTransform: 'uppercase',
                    }}
                    fontWeight={700}
                    gutterBottom
                    color={'secondary'}
                    align={'center'}
                  >
                  Depository Candidates and Committees required to designate a financial institution
                </Typography>
                <Box  
                  sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    padding: 2,
                    borderRadius: 2,
                    boxShadow: 2,
                  }}>
                    <List disablePadding>
                      {candidateOffices.map((office, index) => (
                      <ListItem key={index} disableGutters>
                        <ListItemText
                          primary={office.type}
                          primaryTypographyProps={{variant:'subtitle2', fontWeight:550}}
                          secondary={office.description}
                      />
                      </ListItem>
                    ))}
                    </List>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid size={{xs:12, md:3}}>
            <Box component={Card} bgcolor={'primary.main'} marginTop={isSM?0:3} padding={1} marginBottom={3}>
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    color="text.primary"
                    sx={{ color: 'common.white' }}
                  >
                    Bank Liaison
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    component={'p'} 
                    color="text.secondary"
                    sx={{ color: 'common.white' }}
                  >
                    If you have any questions, or require assistance with your reporting requirements, 
                    please contact <b>Sarabeth McClain</b> at ocpf@mass.gov, or at 617-979-8300.
                  </Typography>
                </CardContent>
            </Box>
            <BankList></BankList>
          </Grid>
        </Grid>
      </Container>
    </Main>
  );
};

export default BankSupport;